import React, { Fragment, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
    useGetSuppliersQuery,
    useGetPurchaseOrdersQuery,
    useGetReturnsSupplierQuery,
    useGetMovementsQuery
} from '../../../../store/services/inventoryApi';
import { Edit, Mail, Phone, MapPin, User, Hash } from 'react-feather';

const SupplierPurchaseOrders = ({ supplierId }) => {
    const { data: pos, isLoading, error } = useGetPurchaseOrdersQuery({ supplierId });

    // Handle both array and object-wrapped responses
    const poList = useMemo(() => {
        if (!pos) return [];
        if (Array.isArray(pos)) return pos;
        if (Array.isArray(pos.data)) return pos.data;
        return [];
    }, [pos]);

    const columns = [
        // ... (rest of columns same)
        {
            name: 'PO Number',
            selector: row => row.poNumber,
            sortable: true,
        },
        {
            name: 'Date',
            selector: row => row.poDate,
            sortable: true,
        },
        {
            name: 'Total Items',
            selector: row => row.totalQuantity,
            sortable: true,
        },
        {
            name: 'Total Cost',
            selector: row => `$${row.totalCost?.toFixed(2)}`,
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <Badge color={
                    row.status === 'RECEIVED' ? 'success' :
                        row.status === 'CANCELLED' ? 'danger' :
                            row.status === 'APPROVED' ? 'info' : 'warning'
                }>
                    {row.status}
                </Badge>
            )
        }
    ];

    return (
        <div className="table-responsive">
            <DataTable
                columns={columns}
                data={poList}
                pagination
                progressPending={isLoading}
                noDataComponent={<div className="p-3 text-muted text-center">No Purchase Orders found for this supplier.</div>}
                highlightOnHover
                striped
            />
        </div>
    );
};

const SupplierReturns = ({ supplierId }) => {
    const { data: returns, isLoading, error } = useGetReturnsSupplierQuery({ supplierId });

    // Handle both array and object-wrapped responses
    const returnList = useMemo(() => {
        if (!returns) return [];
        return Array.isArray(returns) ? returns : (returns.data || []);
    }, [returns]);

    const columns = [
        {
            name: 'Return #',
            selector: row => row.returnNumber,
            sortable: true,
        },
        {
            name: 'Date',
            selector: row => row.returnDate,
            sortable: true,
        },
        {
            name: 'Reason',
            selector: row => row.returnReason,
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <Badge color={row.status === 'COMPLETED' ? 'success' : 'warning'}>
                    {row.status}
                </Badge>
            )
        }
    ];

    return (
        <div className="table-responsive">
            <DataTable
                columns={columns}
                data={returnList}
                pagination
                progressPending={isLoading}
                noDataComponent={<div className="p-3 text-muted text-center">No Returns found for this supplier.</div>}
                highlightOnHover
                striped
            />
        </div>
    );
};

const SupplierActivityLog = ({ supplierId }) => {
    // Note: Activity log might need specific endpoint or parameters
    const { data: movements, isLoading, error } = useGetMovementsQuery({ supplierId });

    // Handle movements structure: { data: { movements: [...] } }
    const movementList = useMemo(() => {
        if (!movements) return [];
        if (Array.isArray(movements)) return movements;
        if (Array.isArray(movements.data)) return movements.data;
        if (movements.data && Array.isArray(movements.data.movements)) return movements.data.movements;
        return [];
    }, [movements]);

    const columns = [
        {
            name: 'Date',
            selector: row => row.createdAt || row.date,
            sortable: true,
        },
        {
            name: 'Type',
            selector: row => row.type,
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || row.notes,
            sortable: false,
            grow: 2,
        },
        {
            name: 'Reference',
            selector: row => row.referenceNumber || row.poNumber || row.receiptNumber,
            sortable: true,
        }
    ];

    return (
        <div className="table-responsive">
            <DataTable
                columns={columns}
                data={movementList}
                pagination
                progressPending={isLoading}
                noDataComponent={<div className="p-3 text-muted text-center">No recent activity found.</div>}
                highlightOnHover
                striped
            />
        </div>
    );
};

const SupplierDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: suppliersResult, isLoading, error } = useGetSuppliersQuery();

    // Find supplier from the list
    const supplier = useMemo(() => {
        if (!suppliersResult) return null;
        const list = Array.isArray(suppliersResult) ? suppliersResult : (suppliersResult.data || []);
        return list.find(s => s._id === id || s.id === id || s.supplierId === id);
    }, [suppliersResult, id]);
    const [activeTab, setActiveTab] = React.useState('1');

    if (isLoading) return <div className="p-5 text-center">Loading supplier details...</div>;
    if (error) {
        console.error('Supplier Details API Error:', error);
        return <div className="p-5 text-center text-danger">Error loading supplier details.</div>;
    }
    if (!supplier) return <div className="p-5 text-center">Supplier not found.</div>;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Supplier Details" parent="Inventory" title="Supplier Details" />
            <Container fluid={true}>
                <Row>
                    <Col xl="4" lg="5">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5>Profile</h5>
                                <Button color="warning" size="xs" onClick={() => navigate(`/inventory/suppliers/edit/${supplier.supplierId}`)}>
                                    <Edit size={14} className="me-1" /> Edit
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <div className="text-center mb-4">
                                    <div className="bg-light p-4 rounded-circle d-inline-block mb-3">
                                        <User size={40} />
                                    </div>
                                    <h4>{supplier.name}</h4>
                                    {supplier.aliasName && <h6 className="text-muted mb-2">Alias: {supplier.aliasName}</h6>}
                                    <p className="text-muted">{supplier.code}</p>
                                    <Badge color={supplier.status?.toUpperCase() === 'ACTIVE' ? 'success' : 'danger'}>
                                        {supplier.status || 'ACTIVE'}
                                    </Badge>
                                </div>
                                <div className="contact-info">
                                    <div className="d-flex align-items-center mb-3">
                                        <Hash size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">ABN</p>
                                            <p className="mb-0">{supplier.abn}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center mb-3">
                                        <User size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Contact Person</p>
                                            <p className="mb-0">{supplier.contactName || supplier.contactPersonName}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center mb-3">
                                        <Mail size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Email</p>
                                            <p className="mb-0">{supplier.contactEmail || supplier.email}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center mb-3">
                                        <Phone size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Phone</p>
                                            <p className="mb-0">{supplier.contactPhone || supplier.phone}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <MapPin size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Address</p>
                                            <p className="mb-0">
                                                {supplier.address?.street || supplier.address?.streetAddress || supplier.address?.addressLine1}<br />
                                                {supplier.address?.addressLine2 && <>{supplier.address.addressLine2}<br /></>}
                                                {supplier.address?.city}, {supplier.address?.state} {supplier.address?.postalCode}<br />
                                                {supplier.address?.country}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl="8" lg="7">
                        <Card>
                            <CardHeader>
                                <Nav tabs>
                                    <NavItem>
                                        <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')}>
                                            Purchase Orders
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')}>
                                            Returns
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={activeTab === '3' ? 'active' : ''} onClick={() => setActiveTab('3')}>
                                            Activity Log
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </CardHeader>
                            <CardBody>
                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        <SupplierPurchaseOrders supplierId={id} />
                                    </TabPane>
                                    <TabPane tabId="2">
                                        <SupplierReturns supplierId={id} />
                                    </TabPane>
                                    <TabPane tabId="3">
                                        <SupplierActivityLog supplierId={id} />
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default SupplierDetails;
